/**
 * useWebSocket Hook
 * React hook for managing WebSocket connections with auto-reconnect
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { WSEventType } from '@/types';
import { transformSnakeToCamel } from '@/lib/api';
import { logger } from '@/lib/logger';

/** Map WebSocket instances to connection IDs (avoids extending WebSocket with custom props) */
const wsConnectionIds = new WeakMap<WebSocket, number>();

/** Re-export for consumers that import from this hook */
export { WSEventType };

/**
 * WebSocket message structure
 */
export interface WSMessage<T = unknown> {
  event: string;
  data: T;
}

/**
 * Connection state
 */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

/**
 * Options for the useWebSocket hook
 */
export interface UseWebSocketOptions {
  /** JWT token for authenticated connections */
  token?: string;
  /** Rooms to join on connection */
  rooms?: string[];
  /** Callback for incoming messages */
  onMessage?: (message: WSMessage) => void;
  /** Callback when connection opens */
  onOpen?: () => void;
  /** Callback when connection closes */
  onClose?: () => void;
  /** Callback when connection error occurs */
  onError?: (error: Event) => void;
  /** Base delay (ms) for exponential backoff; delay is min(reconnectInterval * 1.5^attempt, 30000) */
  reconnectInterval?: number;
  /** Maximum reconnection attempts (0 for infinite) */
  maxReconnectAttempts?: number;
  /** Enable automatic reconnection */
  autoReconnect?: boolean;
  /** Ping interval to keep connection alive (ms) */
  pingInterval?: number;
}

/**
 * Return type for the useWebSocket hook
 */
export interface UseWebSocketReturn {
  /** Current connection state */
  connectionState: ConnectionState;
  /** Whether the connection is currently open */
  isConnected: boolean;
  /** The last received message (may skip earlier messages in the same React batch; prefer drainQueuedMessages for ordered processing) */
  lastMessage: WSMessage | null;
  /**
   * Increments when any message is received. Subscribe in useEffect deps and call
   * {@link drainQueuedMessages} to process every message (avoids losing rapid back-to-back events).
   */
  messageQueueTick: number;
  /** Remove and return all messages buffered since the last drain (FIFO). */
  drainQueuedMessages: () => WSMessage[];
  /** Current rooms the connection is in */
  rooms: string[];
  /** Number of reconnection attempts */
  reconnectAttempts: number;
  /** Send a message through the WebSocket */
  sendMessage: (message: object) => void;
  /** Join additional rooms */
  joinRooms: (rooms: string[]) => void;
  /** Leave rooms */
  leaveRooms: (rooms: string[]) => void;
  /** Send a ping to keep connection alive */
  ping: () => void;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
  /** Increments after each successful reconnect (not the initial connect). */
  reconnectedTick: number;
  /** Last time (ms since epoch) we received a JSON `pong` from the server; null before first pong. */
  lastPongAt: number | null;
}

/**
 * React hook for WebSocket connection with auto-reconnect and room management
 * 
 * @example
 * ```tsx
 * const { isConnected, lastMessage, sendMessage } = useWebSocket({
 *   token: authToken,
 *   rooms: ['public', 'moderators'],
 *   onMessage: (msg) => console.log('Received:', msg),
 * });
 * ```
 */
export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const {
    token,
    rooms: initialRooms = ['public'],
    onMessage,
    onOpen,
    onClose,
    onError,
    reconnectInterval = 3000,
    maxReconnectAttempts = 0,
    autoReconnect = true,
    pingInterval = 30000,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WSMessage | null>(null);
  const [messageQueueTick, setMessageQueueTick] = useState(0);
  const [rooms, setRooms] = useState<string[]>(initialRooms);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [reconnectedTick, setReconnectedTick] = useState(0);
  const [lastPongAt, setLastPongAt] = useState<number | null>(null);

  const messageQueueRef = useRef<WSMessage[]>([]);
  const lastPongRef = useRef<number>(Date.now());
  const lastReceivedMessageTsRef = useRef<number>(0);
  const hasEverConnectedRef = useRef(false);

  const drainQueuedMessages = useCallback((): WSMessage[] => {
    const batch = messageQueueRef.current;
    messageQueueRef.current = [];
    return batch;
  }, []);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const connectionStateRef = useRef<ConnectionState>('disconnected');
  const connectionIdRef = useRef(0); // Track connection attempts to identify stale connections
  
  // Keep ref in sync with state
  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);
  
  // Store callbacks in refs to prevent reconnection loops
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onMessageRef.current = onMessage;
    onOpenRef.current = onOpen;
    onCloseRef.current = onClose;
    onErrorRef.current = onError;
  }, [onMessage, onOpen, onClose, onError]);
  
  // Sync reconnectAttempts state with ref
  useEffect(() => {
    reconnectAttemptsRef.current = reconnectAttempts;
  }, [reconnectAttempts]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  // Build WebSocket URL
  const wsUrl = useMemo(() => {
    // Dev override: ?api=localhost forces ws://localhost:8000 (same as API client)
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      const params = window.location?.search ? new URLSearchParams(window.location.search) : null;
      if (params?.get('api') === 'localhost') {
        const baseUrl = 'ws://localhost:8000';
        const url = new URL(baseUrl);
        url.pathname = '/ws';
        return url.toString();
      }
    }
    // When opening the frontend from another device via LAN IP, `localhost`
    // refers to that device. Derive a sane default from the current hostname.
    const derivedDefault =
      typeof window !== 'undefined' &&
      window.location?.hostname &&
      window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1'
        ? `ws://${window.location.hostname}:8000`
        : 'ws://localhost:8000';

    const baseUrl = import.meta.env.VITE_WS_URL || derivedDefault;
    const url = new URL(baseUrl);
    
    // Ensure /ws path is present (backend expects /ws endpoint)
    if (!url.pathname || url.pathname === '/') {
      url.pathname = '/ws';
    } else if (!url.pathname.endsWith('/ws')) {
      // If pathname exists but doesn't end with /ws, append it
      url.pathname = url.pathname.endsWith('/') 
        ? url.pathname + 'ws' 
        : url.pathname + '/ws';
    }
    // Do not put token in query string (avoids token in server/proxy logs).
    // Token is sent via first message { action: 'auth', token } after connection.
    const finalUrl = url.toString();
    return finalUrl;
  }, [token]);

  // Send message through WebSocket
  const sendMessage = useCallback((message: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }, []);

  // Join rooms
  const joinRooms = useCallback((roomsToJoin: string[]) => {
    sendMessage({ action: 'join', rooms: roomsToJoin });
  }, [sendMessage]);

  // Leave rooms
  const leaveRooms = useCallback((roomsToLeave: string[]) => {
    sendMessage({ action: 'leave', rooms: roomsToLeave });
  }, [sendMessage]);

  // Send ping
  const ping = useCallback(() => {
    sendMessage({ action: 'ping' });
  }, [sendMessage]);

  // Store options in refs to avoid recreating connect function
  const optionsRef = useRef({
    initialRooms,
    autoReconnect,
    maxReconnectAttempts,
    reconnectInterval,
    pingInterval,
  });

  useEffect(() => {
    optionsRef.current = {
      initialRooms,
      autoReconnect,
      maxReconnectAttempts,
      reconnectInterval,
      pingInterval,
    };
  }, [initialRooms, autoReconnect, maxReconnectAttempts, reconnectInterval, pingInterval]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if already connecting or connected
    if (wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Prevent multiple simultaneous connection attempts (use ref to avoid stale closure)
    const currentState = connectionStateRef.current;
    if (currentState === 'connecting' || currentState === 'reconnecting') {
      return;
    }

    connectionStateRef.current = 'connecting';
    setConnectionState('connecting');
    
    // Increment connection ID to identify this connection attempt
    const connectionId = ++connectionIdRef.current;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      wsConnectionIds.set(ws, connectionId);

      ws.onopen = () => {
        if (wsRef.current !== ws || wsConnectionIds.get(ws) !== connectionId) {
          ws.close(); // Close this old/stale connection
          return;
        }
        
        // Clear any pending reconnection timers since we're now connected
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        connectionStateRef.current = 'connected';
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;
        setReconnectAttempts(0);

        lastPongRef.current = Date.now();
        setLastPongAt(Date.now());

        const wasReconnect = hasEverConnectedRef.current;
        if (wasReconnect) {
          setReconnectedTick((t) => t + 1);
        }

        const openOpts = optionsRef.current;
        // Prefer auth via first message (avoids token in URL/server logs)
        if (token) {
          ws.send(JSON.stringify({ action: 'auth', token }));
        } else if (openOpts.initialRooms.length > 0) {
          ws.send(JSON.stringify({ action: 'join', rooms: openOpts.initialRooms }));
        }

        if (wasReconnect && lastReceivedMessageTsRef.current > 0) {
          ws.send(
            JSON.stringify({
              action: 'replay',
              since_ts: lastReceivedMessageTsRef.current,
            }),
          );
        }
        hasEverConnectedRef.current = true;

        // Start ping interval (JSON ping + stale-pong detection)
        if (openOpts.pingInterval > 0) {
          const pongGraceMs = 10_000;
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) {
              return;
            }
            const maxSilence = openOpts.pingInterval + pongGraceMs;
            if (Date.now() - lastPongRef.current > maxSilence) {
              logger.warn('[useWebSocket] Pong timeout; closing socket to reconnect');
              ws.close();
              return;
            }
            ws.send(JSON.stringify({ action: 'ping' }));
          }, openOpts.pingInterval);
        }

        onOpenRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          // If we're receiving messages, the connection is definitely open
          // Update state to connected if it's not already (handles stuck reconnecting state)
          if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
            if (connectionStateRef.current !== 'connected') {
              connectionStateRef.current = 'connected';
              setConnectionState('connected');
              reconnectAttemptsRef.current = 0;
              setReconnectAttempts(0);
              // Clear any pending reconnection timers since we're connected
              if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
              }
            }
          }
          
          const rawMessage = JSON.parse(event.data);
          const message = transformSnakeToCamel(rawMessage) as WSMessage;

          if (message.event === WSEventType.SERVER_SHUTDOWN || message.event === 'serverShutdown') {
            reconnectAttemptsRef.current = 0;
            setReconnectAttempts(0);
          }

          const topTs = (rawMessage as { ts?: unknown }).ts;
          if (typeof topTs === 'number') {
            lastReceivedMessageTsRef.current = Math.max(
              lastReceivedMessageTsRef.current,
              topTs,
            );
          }

          if (message.event === WSEventType.PONG || message.event === 'pong') {
            const now = Date.now();
            lastPongRef.current = now;
            setLastPongAt(now);
          }

          messageQueueRef.current.push(message);
          setLastMessage(message);
          setMessageQueueTick((t) => t + 1);
          
          // Handle rooms_updated or authenticated event (update rooms list)
          if ((message.event === WSEventType.ROOMS_UPDATED || message.event === 'authenticated') &&
              message.data &&
              typeof message.data === 'object' &&
              ('rooms' in message.data || 'rooms' in (rawMessage.data || {}))) {
            const rooms = (message.data as { rooms?: string[] }).rooms ||
                         ((rawMessage.data as { rooms?: string[] })?.rooms);
            if (rooms) {
              setRooms(rooms);
            }
          }
          
          // Handle connected event (check both camelCase and snake_case for compatibility)
          if (message.event === WSEventType.CONNECTED) {
            // Ensure state is set to connected when we receive the CONNECTED event from server
            // This is important because the server sends this after accepting the connection
            if (connectionStateRef.current !== 'connected') {
              connectionStateRef.current = 'connected';
              setConnectionState('connected');
              reconnectAttemptsRef.current = 0;
              setReconnectAttempts(0);
            }
            
            // Update rooms if provided
            if (message.data &&
                typeof message.data === 'object' &&
                ('rooms' in message.data || 'rooms' in (rawMessage.data || {}))) {
              const rooms = (message.data as { rooms?: string[] }).rooms || 
                           ((rawMessage.data as { rooms?: string[] })?.rooms);
              
              if (rooms) {
                setRooms(rooms);
              }
            }
          }

          onMessageRef.current?.(message);
        } catch (error) {
          logger.error('Failed to parse WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        if (wsRef.current !== ws || wsConnectionIds.get(ws) !== connectionId) {
          return; // This is an old/stale connection, ignore
        }
        
        // Check if we're already connected (might have reconnected before this close event)
        // This can happen if the connection closes but a new one already opened
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          // A new connection is already open, don't update state
          return;
        }
        
        // Only update state if we're not already disconnected (prevent infinite loops)
        // Use ref to check current state without causing re-renders
        if (connectionStateRef.current !== 'disconnected') {
          connectionStateRef.current = 'disconnected';
          setConnectionState('disconnected');
        }
        clearTimers();
        onCloseRef.current?.();

        const closeOpts = optionsRef.current;
        // Attempt reconnect if enabled and not manually disconnected
        if (closeOpts.autoReconnect && shouldReconnectRef.current) {
          const currentAttempts = reconnectAttemptsRef.current;
          if (closeOpts.maxReconnectAttempts === 0 || currentAttempts < closeOpts.maxReconnectAttempts) {
            const delayMs = Math.min(
              Math.floor(closeOpts.reconnectInterval * Math.pow(1.5, currentAttempts)),
              30_000,
            );
            // Use setTimeout to break the synchronous update cycle
            setTimeout(() => {
              // Double-check we should still reconnect and haven't already reconnected
              // Also verify this is still the current connection with correct ID
              // IMPORTANT: Check if a new connection is already open before setting to reconnecting
              if (
                shouldReconnectRef.current && 
                wsRef.current === ws &&
                wsConnectionIds.get(wsRef.current) === connectionId &&
                wsRef.current?.readyState !== WebSocket.OPEN &&
                connectionStateRef.current !== 'connecting' &&
                connectionStateRef.current !== 'connected'
              ) {
                connectionStateRef.current = 'reconnecting';
                setConnectionState('reconnecting');
                reconnectAttemptsRef.current = currentAttempts + 1;
                setReconnectAttempts(reconnectAttemptsRef.current);
                
                reconnectTimeoutRef.current = setTimeout(() => {
                  // Final check before connecting - make sure we're still in a state that needs reconnecting
                  if (
                    shouldReconnectRef.current && 
                    connectionStateRef.current !== 'connected' &&
                    wsRef.current?.readyState !== WebSocket.OPEN
                  ) {
                    connect();
                  } else if (wsRef.current?.readyState === WebSocket.OPEN) {
                    // Connection is already open, update state
                    connectionStateRef.current = 'connected';
                    setConnectionState('connected');
                  }
                }, delayMs);
              }
            }, 100);
          }
        }
      };

      ws.onerror = (error) => {
        if (wsRef.current !== ws || wsConnectionIds.get(ws) !== connectionId) {
          return;
        }
        logger.error('[useWebSocket] WebSocket error:', error, { connectionId, wsUrl, readyState: ws.readyState });
        onErrorRef.current?.(error);
        // onclose will be called after onerror
      };
    } catch (error) {
      logger.error('Failed to create WebSocket connection:', error);
      connectionStateRef.current = 'disconnected';
      setConnectionState('disconnected');
    }
  }, [wsUrl, token, clearTimers]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    shouldReconnectRef.current = true;
    setReconnectAttempts(0);
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    connect();
  }, [connect]);

  // Manual disconnect
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    clearTimers();
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    connectionStateRef.current = 'disconnected';
    setConnectionState('disconnected');
    messageQueueRef.current = [];
  }, [clearTimers]);

  // Connect on mount and when wsUrl or token changes.
  // When token becomes available (e.g. after localStorage load on moderator page),
  // we must reconnect so the new connection sends auth and joins moderator rooms.
  useEffect(() => {
    if (wsRef.current) {
      const oldWs = wsRef.current;
      shouldReconnectRef.current = false;
      
      // Remove old handlers to prevent them from firing
      oldWs.onclose = () => {
        // Stale connection - do nothing
      };
      oldWs.onerror = () => {
        // Stale connection - do nothing
      };
      oldWs.onopen = () => {
        // Stale connection - close it immediately
        oldWs.close();
      };
      
      oldWs.close();
      wsRef.current = null;
    }
    
    // Clear any pending reconnect timers
    clearTimers();
    
    // Set flag to allow reconnection for new connection
    shouldReconnectRef.current = true;
    
    // Small delay to ensure old connection is fully closed
    const connectTimeout = setTimeout(() => {
      if (shouldReconnectRef.current) {
        connect();
      }
    }, 150); // Slightly longer delay to ensure cleanup

    return () => {
      clearTimeout(connectTimeout);
      shouldReconnectRef.current = false;
      clearTimers();
      if (wsRef.current) {
        wsConnectionIds.delete(wsRef.current);
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onopen = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [wsUrl, token]);

  // Note: Token changes are handled by wsUrl change (token is in wsUrl dependency)
  // This effect is kept for backwards compatibility but wsUrl change should handle it
  // Removing duplicate logic to prevent race conditions

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    lastMessage,
    messageQueueTick,
    drainQueuedMessages,
    rooms,
    reconnectAttempts,
    sendMessage,
    joinRooms,
    leaveRooms,
    ping,
    reconnect,
    disconnect,
    reconnectedTick,
    lastPongAt,
  };
}

/**
 * Hook for subscribing to specific WebSocket events
 * 
 * @example
 * ```tsx
 * useWebSocketEvent(WSEventType.NEW_PRAYER, (data) => {
 *   console.log('New prayer:', data);
 * });
 * ```
 */
export function useWebSocketEvent<T = unknown>(
  eventType: WSEventType | string,
  callback: (data: T) => void,
  deps: React.DependencyList = []
): void {
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback, ...deps]);
  
  useEffect(() => {
    const handler = (event: CustomEvent<WSMessage<T>>) => {
      if (event.detail.event === eventType) {
        callbackRef.current(event.detail.data);
      }
    };
    
    window.addEventListener('ws-message' as keyof WindowEventMap, handler as EventListener);
    
    return () => {
      window.removeEventListener('ws-message' as keyof WindowEventMap, handler as EventListener);
    };
  }, [eventType]);
}

export default useWebSocket;
